/**
 * Paid-attribution capture for Son's Blue River Camp.
 *
 * Mirrors the contract documented in booking-analytics-attribution.md:
 *  - Capture gclid / fbclid / gbraid / wbraid + UTMs from the URL on app boot.
 *  - Read (or synthesize from fbclid) the Meta Pixel _fbc cookie.
 *  - Persist to localStorage under `brc_attribution` with a 90-day TTL.
 *  - First-touch-wins: organic/direct visits never overwrite paid attribution
 *    within the 90-day window. A genuinely new click ID does replace.
 *  - `getAttribution()` re-reads _fbp / _fbc on every call so a late pixel
 *    write is reflected on the next backend POST.
 */

export type Attribution = {
  gclid?: string | null;
  fbclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbp?: string | null;
  fbc?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_url?: string | null;
  landing_referrer?: string | null;
  captured_at?: string | null;
};

const STORAGE_KEY = "brc_attribution";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const CLICK_ID_KEYS = ["gclid", "fbclid", "gbraid", "wbraid"] as const;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type Stored = { value: Attribution; expires_at: number };

function safeWindow(): Window | null {
  return typeof window === "undefined" ? null : window;
}

function readCookie(name: string): string | null {
  try {
    if (typeof document === "undefined") return null;
    const parts = document.cookie ? document.cookie.split("; ") : [];
    for (const p of parts) {
      const eq = p.indexOf("=");
      if (eq < 0) continue;
      if (p.slice(0, eq) === name) return decodeURIComponent(p.slice(eq + 1));
    }
    return null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    if (typeof document === "undefined") return;
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    // Apex domain attempted by leaving Domain unset — the cookie will live
    // on the current host, which is fine for SPA navigation.
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function readStored(): Stored | null {
  const w = safeWindow();
  if (!w) return null;
  try {
    const raw = w.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed || typeof parsed.expires_at !== "number") return null;
    if (parsed.expires_at < Date.now()) {
      w.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(value: Attribution): void {
  const w = safeWindow();
  if (!w) return;
  try {
    const payload: Stored = { value, expires_at: Date.now() + TTL_MS };
    w.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

function emptyOrNull(s: string | null | undefined): string | null {
  if (s === null || s === undefined) return null;
  const t = String(s).trim();
  return t.length === 0 ? null : t;
}

function parseUrlParams(): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  const w = safeWindow();
  if (!w) return out;
  try {
    const params = new URLSearchParams(w.location.search);
    for (const k of CLICK_ID_KEYS) out[k] = emptyOrNull(params.get(k));
    for (const k of UTM_KEYS) out[k] = emptyOrNull(params.get(k));
  } catch {
    /* ignore */
  }
  return out;
}

function hasAnyClickId(p: Record<string, string | null>): boolean {
  return CLICK_ID_KEYS.some((k) => !!p[k]);
}

/**
 * Call once on app boot, before React renders. Idempotent.
 */
export function captureClickIds(): void {
  const w = safeWindow();
  if (!w) return;

  const fresh = parseUrlParams();
  const existing = readStored();

  // Synthesize _fbc from fbclid if Meta Pixel hasn't written it yet.
  if (fresh.fbclid && !readCookie("_fbc")) {
    writeCookie("_fbc", `fb.1.${Date.now()}.${fresh.fbclid}`, 90);
  }

  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");

  if (existing && !hasAnyClickId(fresh)) {
    // First-touch wins: refresh only the volatile pixel cookies.
    const merged: Attribution = {
      ...existing.value,
      fbp: fbp ?? existing.value.fbp ?? null,
      fbc: fbc ?? existing.value.fbc ?? null,
    };
    writeStored(merged);
    return;
  }

  // New paid click (or no prior record) — replace with fresh blob.
  const next: Attribution = {
    gclid: fresh.gclid ?? null,
    fbclid: fresh.fbclid ?? null,
    gbraid: fresh.gbraid ?? null,
    wbraid: fresh.wbraid ?? null,
    fbp,
    fbc,
    utm_source: fresh.utm_source ?? null,
    utm_medium: fresh.utm_medium ?? null,
    utm_campaign: fresh.utm_campaign ?? null,
    utm_content: fresh.utm_content ?? null,
    utm_term: fresh.utm_term ?? null,
    landing_url: w.location.href,
    landing_referrer: typeof document !== "undefined" ? document.referrer || null : null,
    captured_at: new Date().toISOString(),
  };
  writeStored(next);
}

/**
 * Read the attribution blob attached to every backend POST.
 * Always refreshes _fbp / _fbc from cookies so a late pixel write
 * is reflected on the next call.
 */
export function getAttribution(): Attribution {
  const stored = readStored();
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  if (!stored) {
    return {
      gclid: null,
      fbclid: null,
      gbraid: null,
      wbraid: null,
      fbp,
      fbc,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      landing_url: null,
      landing_referrer: null,
      captured_at: null,
    };
  }
  return { ...stored.value, fbp: fbp ?? stored.value.fbp ?? null, fbc: fbc ?? stored.value.fbc ?? null };
}